import { useRuntimeConfig, useState } from '#imports'
import { computed, onMounted, ref } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { noop } from '@backmarket/utils/function/noop'

import { type Offer, isOffer } from '~/api/tradein-apac/types'

import { apacToBmOffer, bmToApacOffer } from '../adapters/tradeInOffer'

type PersistedOffer = {
  offer: Offer
  timestamp: number
}

/**
 * This is historical implementation of this composable that is used for trade-in AU.
 *
 * With trade-in JP development, persistence of trade-in offer is being integrated directly in the
 * 'useBuybackOffer' of buyback layer which is implemented by 'useTradinAuOfferIntegrated'.
 *
 * More info in trade-in JP tech disco:
 * https://backmarket.atlassian.net/wiki/spaces/APAC/pages/6285918281/Tech+Disco+Trade-in+Japan+MVP#Showing-trade-in-offer-in-pre-sales-page
 *
 * Trade-in AU should get the treatment shortly after. We would be ok to use the new 'integrated'
 * implementation even for AU but we need to be careful not to start showing the trade-in AU offer
 * outside of PDP for now (because the design and components have not been updated for AU yet).
 * Let's do that when working on AU, or in JP follow-up task to prepare the ground for AU.
 */
function useTradeInApacOfferIndependent() {
  const {
    public: { TRADEIN_AU_OFFER_TTL_MS },
  } = useRuntimeConfig()
  const localStorage = useLocalStorage()
  const marketKey = useMarketplace().market.countryCode.toLowerCase()
  const STORAGE_KEY = `tradein_${marketKey}_accepted_offer`
  const DEFAULT_TTL_MS = 3 * 24 * 60 * 60 * 1000
  const offerTtlMs = Number(TRADEIN_AU_OFFER_TTL_MS) || DEFAULT_TTL_MS

  const acceptedOfferRef = useState<Offer | undefined>(
    `tradein-${marketKey}:accepted-offer`,
    () => undefined,
  )

  // Intermediate 'computed' to match typing of 'useTradeInApacOfferIntegrated'.
  const acceptedOffer = computed(() => {
    return acceptedOfferRef.value
  })

  function setAcceptedOffer(offer: Offer) {
    if (!isOffer(offer)) return
    acceptedOfferRef.value = offer
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ offer, timestamp: Date.now() }),
    )
  }

  onMounted(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return

    try {
      const { offer, timestamp } = JSON.parse(stored) as PersistedOffer
      const elapsed = Date.now() - timestamp

      if (elapsed > offerTtlMs) {
        localStorage.removeItem(STORAGE_KEY)

        return
      }

      if (!isOffer(offer)) {
        localStorage.removeItem(STORAGE_KEY)

        return
      }

      acceptedOfferRef.value = offer
    } catch {
      // corrupted data in store - not supposed to happen
      localStorage.removeItem(STORAGE_KEY)
    }
  })

  return {
    acceptedOffer,
    setAcceptedOffer,
    // Properties to match 'useTradeInApacOfferIntegrated' but not implemented by 'useTradeInApacOfferIndependent'.
    deleteAcceptedOffer: noop,
    onPaymentSuccess: noop,
    swappedOffer: ref<Offer | undefined>(),
  }
}

/**
 * Offer swapped after a successful payment ('onPaymentSuccess').
 *
 * Shared state so other scopes (e.g. checkout tracking) can read it without
 * instantiating the full composable.
 */
export function useSwappedApacOffer() {
  return useState<Offer | undefined>(
    'tradein-apac:swapped-offer',
    () => undefined,
  )
}

function useTradeInApacOfferIntegrated() {
  const $httpFetch = useHttpFetch()
  const logger = useLogger()
  const { countryCode } = useMarketplace().market
  const pathParamMarketValue = countryCode.toLowerCase()
  const isSwapAfterCheckoutEnabled =
    useRuntimeConfig().public.FF_TRADEIN_APAC_SWAP_AFTER_CHECKOUT?.includes(
      countryCode,
    ) ?? false
  const {
    buybackOffer,
    deleteBuybackOffer,
    restoreOfferFromLocalStorage,
    saveBuybackOffer,
  } = useBuybackOffer()

  /**
   * Offer accepted by the customer.
   *
   * Derived from the buyback offer state: the whole lifecycle (persistence,
   * restore on mount) is owned by 'useBuybackOffer'. On APAC trade-in markets
   * the offer is persisted in local storage by the buyback layer.
   */
  const acceptedOffer = computed<Offer | undefined>(() => {
    return buybackOffer.value ? bmToApacOffer(buybackOffer.value) : undefined
  })

  /**
   * Offer accepted by the customer after a regular sales order is placed.
   *
   * After placing a regular sales order, 'acceptedOffer' is reset and 'swappedOffer'
   * can be used to keep displaying this offer after that (like on payment confirmation
   * page or for tracking).
   *
   * This does not survive a page refresh but this is considered acceptable as it's only
   * used on payment confirmation page which is not a page where customers go back later.
   * Customers can find instructions to proceed with trade-in next steps in their mailbox.
   */
  const swappedOffer = useSwappedApacOffer()

  function setAcceptedOffer(offer: Offer) {
    saveBuybackOffer(apacToBmOffer(offer), {})
    swappedOffer.value = undefined
  }

  async function createSwapOffer(paymentBusinessReference?: string) {
    if (!swappedOffer.value) {
      throw new Error('Cannot create swap order without a swap order')
    }

    await $httpFetch('/api/apac-trade-in/:market/v1/accept-offer', {
      method: 'POST',
      pathParams: {
        market: pathParamMarketValue,
      },
      body: {
        offer: swappedOffer.value,
        paymentBusinessReference,
      },
    })
  }

  /**
   * To call on a successful payment. Backs up the accepted offer (if any) into
   * 'swappedOffer' before resetting it.
   *
   * Relies on JP being a local-storage market ('cartStorageEnabled: false'):
   * the offer is restored synchronously here (works after a 3DS redirect cold
   * boot, where 'useBuybackOffer' restores 'onMounted'), whereas cart storage
   * fetches it asynchronously and would not be available yet.
   */
  function onPaymentSuccess(params?: {
    paymentBusinessReference?: string
  }): void {
    if (!buybackOffer.value) {
      restoreOfferFromLocalStorage()
    }

    const offer = acceptedOffer.value

    // No trade-in offer accepted, nothing was swapped.
    if (!offer) return

    // Backup before deleting; deletion must not block the caller ('swappedOffer'
    // is set synchronously) and failures are logged instead of thrown.
    swappedOffer.value = offer

    if (isSwapAfterCheckoutEnabled) {
      createSwapOffer(params?.paymentBusinessReference).catch((error) =>
        logger.error('Failed to create APAC swap offer on payment success', {
          error,
          owners: ['bot-squad-apac-front'],
        }),
      )
    }

    deleteBuybackOffer().catch((error) =>
      logger.error('Failed to delete APAC trade-in offer on payment success', {
        error,
        owners: ['bot-squad-apac-front'],
      }),
    )
  }

  return {
    acceptedOffer,
    deleteAcceptedOffer: deleteBuybackOffer,
    onPaymentSuccess,
    setAcceptedOffer,
    swappedOffer,
  }
}

const useTradeInApacOfferImplementation: {
  [K in MarketCountryCode]?: typeof useTradeInApacOfferIntegrated
} = {
  [MarketCountryCode.AU]: useTradeInApacOfferIndependent,
  [MarketCountryCode.JP]: useTradeInApacOfferIntegrated,
}

/**
 * Implementation of 'useTradeInApacOffer' is historically raising an error if used outside
 * APAC markets.
 *
 * If you need to use it in a component that can be used in any market, prefer using
 * this 'useTradeInApacOfferSafe'. It won't raise an error but will return 'null' instead.
 * This way the type checker can force to consider what to do when it's not an APAC market.
 */
export function useTradeInApacOfferSafe() {
  const {
    market: { countryCode },
  } = useMarketplace()

  const implementation = useTradeInApacOfferImplementation[countryCode]

  if (implementation) {
    return implementation()
  }

  return null
}

/**
 * 'useTradeInApac' is more convenient when used in a component that is expected to
 * be loaded only in APAC markets as you don't have to deal with 'null' type.
 */
export function useTradeInApacOffer() {
  const tradeInApacOffer = useTradeInApacOfferSafe()

  if (!tradeInApacOffer) {
    throw new Error(`cannot find useTradeInApacOffer implementation`)
  }

  return tradeInApacOffer
}
